import { engineShortName } from './engine-names.js';

/**
 * Metric snapshots turned into something a chart can draw.
 *
 * Ported from the legacy frontend's `metricsSeries.js`. Two things changed on the way,
 * and both are the reason this is a port and not a copy:
 *
 * - **No apexcharts.** The legacy version cut a line by inserting a `null` point just
 *   before a break, which is a trick that only works in that one library. Here the
 *   series comes out already **split into segments**, and the chart draws one line per
 *   segment. The design system uses a different chart library, and a shape that
 *   depends on one library's rendering quirk is a shape that has to be re-derived the
 *   next time the library changes.
 * - **Nothing is computed.** The three metrics are read from the snapshot Virtual
 *   Profiler already computed. The back-office does not score anything: the
 *   anti-survivorship rule, the score methods and the outcome of each video are all
 *   decided upstream and nothing here second-guesses them.
 *
 * ## The series break, which is the whole point of this file
 *
 * A snapshot carries a `seriesKey`: what it was measured *with* — engine versions,
 * configuration hash, resolved providers. Two snapshots with different keys are not
 * comparable, and joining them with a line would draw an improvement that is really a
 * change of ruler. The break is drawn as a gap, and the chart says what changed.
 */

export type ScoreMethod = 'computed_subtraction' | 'alert_major';

/**
 * The method every screen opens on: «Alerta mayor», the product's main reading.
 *
 * Not the engine's default. Upstream still flattens its legacy fields under the sum — see
 * `FLAT_FIELDS_METHOD` — and the two must not be confused: which method a screen shows first is
 * a product decision, which method an old flat number belongs to is a fact about the data.
 */
export const DEFAULT_SCORE_METHOD: ScoreMethod = 'alert_major';

/**
 * The method upstream's flat, pre-per-method fields mirror: `methods.computed_subtraction`.
 *
 * A number read from those fields is the sum's, whatever method the screen has selected.
 * Labelling it with any other method would show one measurement under another's name.
 */
export const FLAT_FIELDS_METHOD: ScoreMethod = 'computed_subtraction';

/**
 * The score methods in the order a selector offers them: the main one first.
 *
 * One list for every screen. Each used to keep its own copy, and two selectors listing the same
 * methods in a different order read as two different choices.
 */
export const METHOD_ORDER: readonly ScoreMethod[] = Object.freeze([
  'alert_major',
  'computed_subtraction',
]);

/** What each method is called on screen. Keyed by string: upstream may send one this build does not know. */
export const METHOD_LABELS: Readonly<Record<string, string>> = Object.freeze({
  computed_subtraction: 'Resta calculada',
  alert_major: 'Alerta mayor',
});

/** A method's name, or upstream's own spelling for one this build does not know. */
export function methodLabel(method: string): string {
  return METHOD_LABELS[method] ?? method;
}

export type MetricDirection =
  /** Higher is better: the target is a floor. */
  | 'higher'
  /** Lower is better: the target is a ceiling. */
  | 'lower';

export interface MetricDefinition {
  key: string;
  label: string;
  /** Field inside the snapshot's method block. Virtual Profiler's spelling. */
  field: string;
  target: number;
  direction: MetricDirection;
}

/**
 * The three numbers the product is judged on.
 *
 * The targets are **product decisions copied from the legacy tool**, not something
 * measured: lies detected at 70 % or better, false alarms at 2 % or lower, correct
 * videos at 70 % or better. They live here because the screen draws the target line,
 * the same way `DEFAULT_ENGINE_CATALOGUE` lives here — and like that one, they should
 * come from Virtual Profiler the day it serves them, so the goal and the number that
 * is judged against it cannot drift apart.
 */
export const SERIES_METRICS: readonly MetricDefinition[] = Object.freeze([
  {
    key: 'incoherences_detected_pct',
    label: 'Mentiras detectadas',
    field: 'incoherents_detected_pct',
    target: 70,
    direction: 'higher',
  },
  {
    key: 'false_positives_pct',
    label: 'Falsos positivos',
    field: 'truthful_flagged_pct',
    target: 2,
    direction: 'lower',
  },
  {
    key: 'correct_pct',
    label: 'Vídeos correctos',
    field: 'correctness_pct',
    target: 70,
    direction: 'higher',
  },
]);

const metricByKey = new Map(SERIES_METRICS.map((metric) => [metric.key, metric]));

/**
 * A snapshot, loosely typed on purpose.
 *
 * It arrives in two shapes: the full document, and the flattened summary a list sends
 * (`lastSnapshot`). Reading both is cheaper than two types and two readers, and the
 * fields that matter are the same.
 */
export interface MetricSnapshot {
  id?: string | null;
  computedAt?: Date | null;
  batch?: string | null;
  trigger?: string | null;
  /** What this was measured with. Two different keys are two different rulers. */
  seriesKey?: string | null;
  seriesBreak?: boolean;
  seriesInputs?: SeriesInputs | null;
  /** Flattened values, as a list summary sends them. */
  [metricKey: string]: unknown;
  /** The full block, per score method. */
  methods?: Record<string, Record<string, number | null>> | null;
}

export interface SeriesInputs {
  gesturesVersion?: string | null;
  coherenceVersion?: string | null;
  enginesHash?: string | null;
  resolved?: Record<string, unknown> | null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The value of one metric in one snapshot, under one score method.
 *
 * The method block wins over the flattened field. They disagree when the summary was
 * flattened under the default method and the chart is showing the other one — and
 * silently showing the default's number under the other method's label is the kind of
 * wrong that gets acted on.
 */
export function metricValue(
  snapshot: MetricSnapshot | null | undefined,
  metricKey: string,
  method: ScoreMethod = DEFAULT_SCORE_METHOD,
): number | null {
  if (!snapshot) return null;

  const metric = metricByKey.get(metricKey);
  if (!metric) return null;

  const fromMethod = toNumber(snapshot.methods?.[method]?.[metric.field]);
  if (fromMethod !== null) return fromMethod;

  // The flat field is the sum's number: under any other method it would be a mislabel.
  return method === FLAT_FIELDS_METHOD ? toNumber(snapshot[metricKey]) : null;
}

export type MetricStatus = 'ok' | 'ko' | 'none';

/**
 * Whether a value meets its target.
 *
 * `none` and not `ko` when there is no value: a metric that could not be measured has
 * not been missed, and painting it red would have somebody chasing a regression that
 * never happened.
 */
export function metricStatus(metricKey: string, value: unknown): MetricStatus {
  const metric = metricByKey.get(metricKey);
  const number = toNumber(value);

  if (!metric || number === null) return 'none';

  const meets = metric.direction === 'lower' ? number <= metric.target : number >= metric.target;

  return meets ? 'ok' : 'ko';
}

/**
 * Signed difference between two values: `+3.2 pts`, `−1 pts`, `0 pts`, `—`.
 *
 * A real minus sign, not a hyphen: the column is read beside numbers and a hyphen is
 * narrower than a plus, so a column of deltas visibly fails to line up.
 */
export function formatDelta(
  from: unknown,
  to: unknown,
  { digits = 1, unit = 'pts' }: { digits?: number; unit?: string } = {},
): string {
  const a = toNumber(from);
  const b = toNumber(to);

  if (a === null || b === null) return '—';

  const delta = Math.round((b - a) * 10 ** digits) / 10 ** digits;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';

  return `${sign}${Math.abs(delta)} ${unit}`;
}

/**
 * Snapshots in order, each flagged when it was measured with a different ruler.
 *
 * The first is never a break — there is nothing before it to be incomparable with. A
 * flag the API already set is kept: it knows about changes this side cannot see.
 */
export function markSeriesBreaks(
  snapshots: readonly MetricSnapshot[] | null | undefined,
): MetricSnapshot[] {
  const ordered = (Array.isArray(snapshots) ? snapshots : [])
    .filter(Boolean)
    .map((snapshot) => ({ ...snapshot }))
    .sort((a, b) => (a.computedAt?.getTime() ?? 0) - (b.computedAt?.getTime() ?? 0));

  return ordered.map((snapshot, index) => {
    if (index === 0) return { ...snapshot, seriesBreak: false };

    const previous = ordered[index - 1] as MetricSnapshot;
    const keyChanged =
      snapshot.seriesKey != null &&
      previous.seriesKey != null &&
      snapshot.seriesKey !== previous.seriesKey;

    return { ...snapshot, seriesBreak: Boolean(snapshot.seriesBreak) || keyChanged };
  });
}

/** Runs of consecutive snapshots that share a ruler. A new one starts at each break. */
export function splitSegments(
  snapshots: readonly MetricSnapshot[] | null | undefined,
): MetricSnapshot[][] {
  const segments: MetricSnapshot[][] = [];

  for (const snapshot of markSeriesBreaks(snapshots)) {
    if (segments.length === 0 || snapshot.seriesBreak) segments.push([]);
    (segments[segments.length - 1] as MetricSnapshot[]).push(snapshot);
  }

  return segments;
}

export interface SeriesPoint {
  at: Date | null;
  value: number | null;
  snapshotId: string | null;
  batch: string | null;
  seriesKey: string | null;
  inputs: SeriesInputs | null;
}

export interface MetricSeries {
  key: string;
  label: string;
  target: number;
  direction: MetricDirection;
  /**
   * One array per stretch of comparable snapshots.
   *
   * The chart draws a line per segment, so the gap at a break is the absence of a
   * line rather than a null point somebody has to remember not to connect.
   */
  segments: SeriesPoint[][];
}

export function buildMetricSeries(
  snapshots: readonly MetricSnapshot[] | null | undefined,
  {
    method = DEFAULT_SCORE_METHOD,
    metrics = null,
  }: { method?: ScoreMethod; metrics?: readonly string[] | null } = {},
): MetricSeries[] {
  const segments = splitSegments(snapshots);
  const wanted = metrics?.length ? metrics : SERIES_METRICS.map((metric) => metric.key);

  return wanted
    .map((key) => metricByKey.get(key))
    .filter((metric): metric is MetricDefinition => Boolean(metric))
    .map((metric) => ({
      key: metric.key,
      label: metric.label,
      target: metric.target,
      direction: metric.direction,
      segments: segments.map((segment) =>
        segment.map((snapshot) => ({
          at: snapshot.computedAt ?? null,
          value: metricValue(snapshot, metric.key, method),
          snapshotId: snapshot.id ?? null,
          batch: snapshot.batch ?? null,
          seriesKey: snapshot.seriesKey ?? null,
          inputs: snapshot.seriesInputs ?? null,
        })),
      ),
    }));
}

/**
 * What changed at a break, in words: `Gestos 2.0.3 · Veracidad 1.4.2 · config 9d1c2f3a` — each
 * engine by its short label, never by its identifier.
 *
 * This is what makes a gap in the chart useful instead of alarming. Without it the
 * reader sees the line stop and has no way to know whether something broke or somebody
 * shipped a new engine.
 */
export function describeSeriesInputs(inputs: SeriesInputs | null | undefined): string {
  if (!inputs || typeof inputs !== 'object') return '';

  const parts: string[] = [];

  if (inputs.gesturesVersion) {
    parts.push(`${engineShortName('gestures')} ${inputs.gesturesVersion}`);
  }
  if (inputs.coherenceVersion) {
    parts.push(`${engineShortName('coherence')} ${inputs.coherenceVersion}`);
  }
  if (inputs.enginesHash) parts.push(`config ${inputs.enginesHash.slice(0, 8)}`);

  // The resolved providers, spelled as words: `stt provider whisper`. Which model
  // transcribed is exactly the kind of thing that moves a number without anybody
  // having changed a version.
  for (const key of ['stt_provider', 'stt_model', 'llm_model', 'ruleset_source']) {
    const value = inputs.resolved?.[key];
    if (value) parts.push(`${key.replaceAll('_', ' ')} ${String(value)}`);
  }

  return parts.join(' · ');
}

/**
 * Why a point exists, in the words the screen uses.
 *
 * Worth naming rather than leaving as a raw value, because the three are not variations of the
 * same thing: two of them record an execution and one records that the answer key moved under the
 * group's feet. A chart that showed them alike would have somebody comparing a run against a
 * relabelling and calling the difference progress.
 */
const TRIGGER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  auto: 'Al terminar una ejecución',
  manual: 'Guardada a mano',
  answer_key: 'Cambió la verdad de referencia',
});

export function triggerLabel(trigger: string | null | undefined): string {
  if (!trigger) return 'Sin registrar';

  return TRIGGER_LABELS[trigger] ?? trigger;
}

/**
 * Whether this point records a change in the data rather than a run of the engine.
 *
 * The one thing a reader has to be able to tell apart: the series exists to show the engine
 * getting better, and a point that moved because somebody corrected a label is not that. It is
 * still worth having — without it there is no «before» for the next run to be compared with — but
 * it has to be readable as what it is.
 */
export function measuresTheData(trigger: string | null | undefined): boolean {
  return trigger === 'answer_key';
}
